import { Extension } from '@tiptap/core'
import {
  parseStyleString,
  responsiveVarName,
  stringifyStyleObject
} from '@/plugins/tiptap-v3/functionality/responsive/responsive'

const getBreakpoint = editor => editor?.storage?.responsiveEngine?.breakpoint || 'lg'

const getResponsiveConfig = options => {
  if (!options?.responsive || !options.responsive.enabled) return null

  return {
    prefix: options.responsive.prefix || 'vp',
    cssPropFontWeight: options.responsive.cssPropFontWeight || 'font-weight'
  }
}

export const FontWeight = Extension.create({
  name: 'fontWeight',

  addOptions () {
    return {
      responsive: {
        enabled: true,
        prefix: 'vp',
        cssPropFontWeight: 'font-weight'
      }
    }
  },

  addGlobalAttributes () {
    const cfg = getResponsiveConfig(this.options)

    return [
      {
        types: ['textStyle'],
        attributes: {
          fontWeight: {
            default: null,
            parseHTML: element => element.style.fontWeight || null,
            renderHTML: attributes => {
              if (!attributes.fontWeight) {
                return {}
              }

              // In responsive mode we keep font-weight as CSS vars on `style`.
              // Avoid emitting a fixed inline font-weight from this attribute.
              if (cfg) {
                return {}
              }

              return {
                style: `font-weight: ${attributes.fontWeight}`
              }
            }
          }
        }
      }
    ]
  },

  addCommands () {
    return {
      setFontWeight:
        (weight, opts) =>
          ({ editor, chain }) => {
            const cfg = getResponsiveConfig(this.options)

            // Non-responsive behavior (backwards compatible)
            if (!cfg) {
              return chain()
                .setMark('textStyle', { fontWeight: weight })
                .run()
            }

            const bp = opts?.breakpoint || getBreakpoint(editor)
            const fwVar = responsiveVarName(cfg.cssPropFontWeight, bp, cfg.prefix)

            const attrs = editor.getAttributes('textStyle') || {}
            const styleObj = parseStyleString(attrs.style || '')
            // Legacy compatibility: remove fixed font-weight so responsive vars are authoritative.
            delete styleObj['font-weight']
            styleObj[fwVar] = weight

            return chain()
              .setMark('textStyle', {
                ...attrs,
                style: stringifyStyleObject(styleObj),
                fontWeight: null
              })
              .run()
          },

      unsetFontWeight:
        opts =>
          ({ editor, chain }) => {
            const cfg = getResponsiveConfig(this.options)

            // Non-responsive behavior
            if (!cfg) {
              return chain()
                .setMark('textStyle', { fontWeight: null })
                .removeEmptyTextStyle?.()
                .run()
            }

            const bp = opts?.breakpoint || getBreakpoint(editor)
            const fwVar = responsiveVarName(cfg.cssPropFontWeight, bp, cfg.prefix)

            const attrs = editor.getAttributes('textStyle') || {}
            const styleObj = parseStyleString(attrs.style || '')
            delete styleObj['font-weight']
            delete styleObj[fwVar]

            return chain()
              .setMark('textStyle', {
                ...attrs,
                style: stringifyStyleObject(styleObj),
                fontWeight: null
              })
              .removeEmptyTextStyle?.()
              .run()
          }
    }
  }
})
